<app-confirm-dialog
  #generateConfirmModal
  [title]="'Generated questions will replace current ones'"
  [message]="'You already have questions in this list. Replacing will overwrite them.'"
  [confirmText]="'Replace Questions'"
  [cancelText]="'Cancel'"
  (confirmed)="handleGenerateConfirm($event)"
></app-confirm-dialog>

<app-qualify-demo #qualifyDemo></app-qualify-demo>
<app-battlecards-demo #battlecardsDemo></app-battlecards-demo>

<div class="onboarding-shell">
  <div *ngIf="hasPendingInvite" class="pending-invite-banner">
    <div class="banner-content">
      <fa-icon [icon]="faCheck" class="banner-icon"></fa-icon>
      <span>You have a pending invite from <strong>{{ pendingInviteOrgName }}</strong>. Complete setup to accept it.</span>
    </div>
  </div>
  <div class="onboarding-card" [ngClass]="'step-' + currentStep">

    <!-- Header -->
    <div class="step-header">
      <button
        *ngIf="currentStep > 1"
        type="button"
        class="back-btn-top"
        (click)="goBack()"
        [disabled]="isSaving"
      >
        ← Back
      </button>
      <div class="step-eyebrow">{{ currentStep }} OF {{ totalSteps }}</div>
      <div class="progress-bar">
        <div
          *ngFor="let s of [1,2,3,4,5]"
          class="progress-segment"
          [ngClass]="{
            'done': s < currentStep,
            'current': s === currentStep
          }"
        ></div>
      </div>
    </div>

    <!-- Title -->
    <div class="step-title-block">
      <div class="step-title-row">
        <h1 class="step-title">{{ stepLabel }}</h1>
        <button
          *ngIf="currentStep === 3"
          type="button"
          class="step-help"
          (click)="openQualifyDemo()"
          aria-label="Explain Qualify"
          title="Explain Qualify"
        >
          ?
        </button>
        <button
          *ngIf="currentStep === 4"
          type="button"
          class="step-help"
          (click)="openBattlecardsDemo()"
          aria-label="Explain Battlecards"
          title="Explain Battlecards"
        >
          ?
        </button>
      </div>
      <p class="step-description">{{ stepDescription }}</p>
      <p class="step-sub-description" *ngIf="stepSubDescription">{{ stepSubDescription }}</p>
    </div>

    <div class="step-stage" [@stepTunnel]="currentStep">
    <!-- STEP 1: Workflows -->
    <div class="step-content" *ngIf="currentStep === 1">
      <div class="workflow-grid">
        <div
          *ngFor="let workflow of workflows"
          class="workflow-card"
          [ngClass]="{ active: isWorkflowSelected(workflow.key) }"
          (click)="toggleWorkflow(workflow.key)"
        >
          <div class="workflow-title">{{ workflow.title }}</div>
          <div class="workflow-subtitle">{{ workflow.subtitle }}</div>
        </div>
      </div>
    </div>

    <!-- STEP 2: Framework -->
    <div class="step-content" *ngIf="currentStep === 2">
      <div class="framework-list">
        <div
          *ngFor="let fw of frameworks"
          class="framework-card"
          [ngClass]="{ active: selectedFramework === fw }"
          (click)="selectFramework(fw)"
        >
          <div class="fw-title">{{ fw }}</div>
          <div class="fw-desc" *ngIf="fw === 'BANT'">Budget, authority, need, timing</div>
          <div class="fw-desc" *ngIf="fw === 'MEDDPICC'">Metrics, decision process, criteria, competition, champion, and more</div>
          <div class="fw-desc" *ngIf="fw === 'SPIN'">Situation, problem, implication, need-payoff</div>
          <div class="fw-desc" *ngIf="fw === 'Custom'">Use your own mix of questions</div>
        </div>
      </div>
    </div>

    <!-- STEP 3: Questions - Single Screen -->
    <div class="step-content" *ngIf="currentStep === 3">
      <div class="question-input-form">
        <div class="form-group">
          <label class="form-label">Tell us briefly about what you sell and whom do you sell it to?</label>
          <input
            type="text"
            class="form-input"
            [(ngModel)]="productDescription"
            (ngModelChange)="onInputChange()"
            placeholder="example: I am part of the enterprise sales team at Salesforce, focusing on Data Cloud. Our typical buyers are CIOs..."
            [disabled]="isGenerating"
          />
        </div>

        <div class="form-group">
          <label class="form-label">Typical discovery call duration</label>
          <div class="duration-chips">
            <button
              type="button"
              class="duration-chip"
              [class.active]="callDuration === '15'"
              (click)="selectCallDuration('15')"
              [disabled]="isGenerating"
            >
              15 min
            </button>
            <button
              type="button"
              class="duration-chip"
              [class.active]="callDuration === '30'"
              (click)="selectCallDuration('30')"
              [disabled]="isGenerating"
            >
              30 min
            </button>
            <button
              type="button"
              class="duration-chip"
              [class.active]="callDuration === '45'"
              (click)="selectCallDuration('45')"
              [disabled]="isGenerating"
            >
              45 min
            </button>
            <button
              type="button"
              class="duration-chip"
              [class.active]="callDuration === '60+'"
              (click)="selectCallDuration('60+')"
              [disabled]="isGenerating"
            >
              60+ min
            </button>
          </div>
        </div>

        <button
          *ngIf="shouldShowGenerateButton"
          type="button"
          class="generate-questions-btn"
          [disabled]="isGenerating || !productDescription.trim()"
          (click)="generateQuestionsFromInputs()"
        >
          <span *ngIf="isGenerating" class="spinner-sm"></span>
          <span *ngIf="!isGenerating">{{ questions.length === 0 ? 'Generate questions →' : 'Regenerate questions →' }}</span>
          <span *ngIf="isGenerating">Generating...</span>
        </button>
      </div>

      <!-- Question rows - shown below inputs after generation -->
      <div class="question-builder" *ngIf="questions.length > 0" cdkDropList (cdkDropListDropped)="onQuestionDrop($event)">
        <div 
          *ngFor="let q of questions; let i = index; trackBy: trackByQuestion" 
          class="builder-row"
          [ngClass]="shouldAnimateQuestions ? 'builder-row--animate stagger-' + i : ''"
          cdkDrag
          cdkDragLockAxis="y"
          cdkDragPreviewContainer="parent"
        >
          <div class="drag-handle" cdkDragHandle>⋮⋮</div>
          <div class="q-number">{{ i + 1 }}.</div>
          <!-- The wrapper's ::after mirrors the text and sets the row height,
               so the textarea always grows to fit. See .question-input-wrap. -->
          <div class="question-input-wrap" [attr.data-value]="q.text">
            <textarea
              class="question-input"
              rows="1"
              [(ngModel)]="q.text"
              (ngModelChange)="onQuestionTextChange(q)"
              placeholder="Enter your question"
            ></textarea>
          </div>
          <span class="q-badge">{{ formatCategory(q.category) }}</span>
          <button class="q-remove" (click)="removeQuestion(i)">✕</button>
        </div>
      </div>

      <!-- Add question -->
      <div class="add-question-row" *ngIf="questions.length > 0">
        <input
          class="add-question-input"
          placeholder="Add another question"
          [(ngModel)]="newQuestionText"
          (keydown.enter)="addQuestion()"
          [disabled]="isSaving"
        />
        <button class="btn-secondary" (click)="addQuestion()" [disabled]="isSaving" [class.loading]="isSaving">
          <span *ngIf="!isSaving">Add</span>
          <span *ngIf="isSaving" class="loading-dots">
            <span></span><span></span><span></span>
          </span>
        </button>
      </div>
    </div>

    <!-- STEP 4: Sources -->
    <div class="step-content" *ngIf="currentStep === 4">
      <div class="web-sources-card">
        <div class="ws-title-row">
          <div class="ws-title">Add priority URLs</div>
          <button
            *ngIf="isFreeEmailDomain"
            type="button"
            class="step-help"
            (click)="toggleFreeEmailTooltip()"
            aria-label="Why free emails need URLs"
            title="Why free emails need URLs"
          >
            ?
          </button>
          <div class="free-email-tooltip" *ngIf="showFreeEmailTooltip && isFreeEmailDomain">
            <span class="free-email-tooltip-text">Free email domains (like {{ userDefaultDomain }}) aren't prioritized. Add a custom URL to continue.</span>
            <button type="button" class="tooltip-close" (click)="closeFreeEmailTooltip()" aria-label="Close free email tooltip">✕</button>
          </div>
        </div>
        <div class="ws-desc">Help convverse prioritize from your websites and docs.</div>

        <div class="ws-list">
          <div *ngFor="let source of webSources; let i = index" class="ws-row">
            <div class="ws-check">✓</div>
            <div class="ws-info">
              <div class="ws-domain">{{ source }}</div>
              <div class="ws-sub" *ngIf="source === userDefaultDomain">Detected from your work email domain</div>
            </div>
            <span class="ws-badge">Selected</span>
            <button
              type="button"
              class="icon-remove"
              (click)="removeWebSource(i)"
              [disabled]="isSaving"
              aria-label="Remove URL"
            >
              ✕
            </button>
          </div>
        </div>

        <div class="ws-add-row">
          <input
            class="ws-add-input"
            placeholder="Add another URL, e.g. docs.company.com"
            [(ngModel)]="newWebSource"
            (keydown.enter)="addWebSource()"
          />
          <button class="btn-secondary" (click)="addWebSource()" [disabled]="isSaving" [class.loading]="isSaving">
            <span *ngIf="!isSaving">{{ addWebSourceLabel }}</span>
            <span *ngIf="isSaving" class="loading-dots">
              <span></span><span></span><span></span>
            </span>
          </button>
        </div>
      </div>

      <!-- Upload dropzone (optional) -->
      <div
        class="dropzone"
        (dragover)="onDragOver($event)"
        (drop)="onDrop($event)"
      >
        <div class="dropzone-inner">
          <div class="dropzone-icon">
            <fa-icon [icon]="faCloudArrowUp"></fa-icon>
          </div>
          <div class="dropzone-title-row">
            <div class="dropzone-title">Upload PDFs</div>
            <span class="dropzone-optional">Optional</span>
          </div>
          <div class="dropzone-desc">
            Recommended: much of this isn't on the public web, so your own docs
            make convverse's answers faster and more accurate.
          </div>
          <div class="dropzone-hint">FAQs, pricing sheets, battlecards, case studies, and product docs</div>
          <label class="btn-primary upload-btn" [class.loading]="isUploading">
            <span *ngIf="!isUploading">Select files</span>
            <span *ngIf="isUploading" class="loading-dots">
              <span></span><span></span><span></span>
            </span>
            <input
              type="file"
              accept="application/pdf,.pdf"
              multiple
              style="display:none"
              (change)="onFileSelect($event)"
              [disabled]="isUploading"
            />
          </label>
          <div class="upload-count" *ngIf="uploadedFileCount > 0">
            {{ uploadedFileCount }} file{{ uploadedFileCount > 1 ? 's' : '' }} uploaded
          </div>
        </div>

        <!-- Uploaded files — shown inside the dropzone box -->
        <div class="files-container" *ngIf="pendingUploads.length || uploadedFiles.length">
          <div class="processing-hint" *ngIf="hasProcessingFiles">
            <i class="fa-solid fa-circle-info"></i>
            <span>Processing runs in the background and may take a few moments. You can continue.</span>
          </div>
          <ul class="files-list">
            <ng-container *ngIf="pendingUploads.length">
              <li *ngFor="let pending of pendingUploads" class="file-item pending-row" [class.error-row]="pending.status === 'error'">
                <div class="file-info">
                  <div class="file-header">
                    <span class="file-name muted">
                      <span class="file-name-text">{{ pending.name }}</span>
                    </span>
                    <span class="file-status" [ngClass]="pending.status === 'error' ? 'status-failed' : 'status-processing'">
                      {{ pending.status === 'error' ? 'error' : 'uploading' }}
                    </span>
                  </div>
                  <div class="pending-messages">
                    <span class="file-note primary-message">{{ pending.uploadError ? pending.uploadError : pending.note }}</span>
                    <span class="file-note secondary-message" *ngIf="pending.uploadError">{{ pending.note }}</span>
                  </div>
                </div>
                <div class="pending-actions" *ngIf="pending.status === 'error'; else uploadingControls">
                  <button type="button" class="btn-primary" (click)="retryPendingUpload(pending)" [disabled]="isSaving" [class.loading]="isSaving">
                    <span *ngIf="!isSaving">Retry</span>
                    <span *ngIf="isSaving" class="loading-dots">
                      <span></span><span></span><span></span>
                    </span>
                  </button>
                  <button
                    type="button"
                    class="icon-remove"
                    (click)="dismissPendingError(pending)"
                    [disabled]="isSaving"
                    aria-label="Dismiss upload error"
                  >
                    ✕
                  </button>
                </div>
                <ng-template #uploadingControls>
                  <div class="pending-progress">
                    <span class="progress-indicator"></span>
                  </div>
                </ng-template>
              </li>
            </ng-container>

            <li *ngFor="let file of uploadedFiles" class="file-item">
              <div class="file-info">
                <div class="file-header">
                  <span class="file-name" (click)="downloadFile(file)" [attr.title]="file.name">
                    <span class="file-name-text">{{ file.name }}</span>
                  </span>
                  <span class="file-status" [ngClass]="getStatusClass(file.status || 'pending')" [title]="file.error_message || ''">
                    {{ file.status || 'pending' }}
                  </span>
                </div>
                <span class="file-note">{{ file.note }}</span>
              </div>
              <button
                type="button"
                class="icon-remove"
                (click)="deleteUploadedFile(file)"
                [disabled]="isSaving"
                aria-label="Remove file"
              >
                ✕
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- STEP 5: CRM -->
    <div class="step-content" *ngIf="currentStep === 5">
      <div class="crm-list">
        <div *ngFor="let crm of crmProviders" class="crm-card">
          <div class="crm-info">
          <!-- Salesforce -->
          <div class="crm-brand-row" *ngIf="crm.key === 'salesforce'">
            <span class="ob-crm-logo ob-crm-logo--salesforce" aria-hidden="true">
              <i class="fa-brands fa-salesforce"></i>
            </span>
            <span class="ob-crm-name">Salesforce</span>
          </div>

          <!-- HubSpot -->
          <div class="crm-brand-row" *ngIf="crm.key === 'hubspot'">
            <span class="ob-crm-logo ob-crm-logo--hubspot" aria-hidden="true">
              <i class="fa-brands fa-hubspot"></i>
            </span>
            <span class="ob-crm-name">HubSpot</span>
          </div>

          <!-- Zoho — custom image -->
          <div class="crm-brand-row" *ngIf="crm.key === 'zoho'">
            <span class="ob-crm-logo ob-crm-logo--zoho" aria-hidden="true">
              <span class="zoho-logo-icon"></span>
            </span>
            <span class="ob-crm-name">Zoho</span>
          </div>
          </div>
          <button
            class="btn-crm"
            [ngClass]="crm.colorClass"
            [disabled]="isCrmLocked(crm.key) || isCrmConnecting"
            (click)="connectCrm(crm.key)"
            [class.loading]="isCrmConnecting && crmConnectingProvider === crm.key"
          >
            <span *ngIf="!isCrmConnecting || crmConnectingProvider !== crm.key">
              {{ getCrmButtonLabel(crm.key) }}
            </span>
            <span *ngIf="isCrmConnecting && crmConnectingProvider === crm.key" class="loading-dots">
              <span></span><span></span><span></span>
            </span>
          </button>
        </div>

        <!-- Pipedrive — Coming soon -->
        <div class="crm-card crm-card--disabled">
          <div class="crm-info">
            <div class="crm-brand-row">
              <span class="ob-crm-logo ob-crm-logo--pipedrive" aria-hidden="true">
                <span class="pipedrive-logo-icon"></span>
              </span>
              <span class="ob-crm-name">Pipedrive</span>
            </div>
          </div>
          <button class="btn-crm crm-pipedrive" disabled>Coming soon</button>
        </div>
      </div>
    </div>
    </div>

    <!-- Footer -->
    <div class="step-footer" *ngIf="currentStep !== 3 || questions.length > 0">
      <button
        class="btn-primary btn-full-width"
        *ngIf="currentStep === 1 || currentStep === 2"
        [disabled]="!canProceed || isSaving"
        (click)="goNext()"
        [class.loading]="isSaving"
      >
        <span *ngIf="!isSaving">Continue</span>
        <span *ngIf="isSaving" class="loading-dots">
          <span></span><span></span><span></span>
        </span>
      </button>
      <button
        class="btn-primary btn-full-width"
        *ngIf="currentStep === 3 || currentStep === 4"
        [disabled]="!canProceed || isSaving"
        (click)="goNext()"
        [class.loading]="isSaving"
      >
        <span *ngIf="!isSaving">Continue</span>
        <span *ngIf="isSaving" class="loading-dots">
          <span></span><span></span><span></span>
        </span>
      </button>
      <button
        class="btn-primary btn-full-width finish-btn"
        *ngIf="currentStep === totalSteps"
        [disabled]="isSaving"
        (click)="finishSetup()"
        [class.loading]="isSaving"
      >
        <span *ngIf="!isSaving">
          Finish setup
          <fa-icon [icon]="faArrowRight"></fa-icon>
        </span>
        <span *ngIf="isSaving" class="loading-dots">
          <span></span><span></span><span></span>
        </span>
      </button>
    </div>

    <!-- Setup Complete Page -->
    <div class="setup-complete-container" *ngIf="setupComplete">
      <div class="confetti-container">
        <div
          class="confetti"
          *ngFor="let i of confettiArray; let idx = index"
          [style.--delay]="(idx * 0.06) + 's'"
          [style.--x]="((idx * 13) % 100) + '%'"
          [style.--duration]="(2.8 + (idx % 6) * 0.35) + 's'"
          [style.--size]="(6 + (idx % 6) * 2) + 'px'"
          [style.--rotate]="((idx * 47) % 360) + 'deg'"
          [style.--drift]="(((idx % 10) - 5) * 14) + 'px'"
          [style.--hue]="(idx * 29) % 360"
        ></div>
      </div>
      <div class="complete-content">
        <div class="complete-icon-badge">
          <fa-icon [icon]="faCheck" class="complete-icon"></fa-icon>
        </div>
        <h2 class="complete-title">Onboarding complete!</h2>
        <p class="complete-description">You can now head to the dashboard and start using convverse in your meetings.</p>

        <ul class="complete-summary">
          <li class="summary-row">
            <span class="summary-check">✓</span>
            <span class="summary-text">
              {{ questions.length }} qualification question{{ questions.length === 1 ? '' : 's' }} added
            </span>
          </li>
          <li class="summary-row">
            <span class="summary-check">✓</span>
            <span class="summary-text">
              {{ uploadedFileCount }} PDF{{ uploadedFileCount === 1 ? '' : 's' }} uploaded
            </span>
          </li>
          <li class="summary-row" *ngIf="webSources.length > 0">
            <span class="summary-check">✓</span>
            <span class="summary-text">
              {{ webSources.length }} battlecard URL{{ webSources.length === 1 ? '' : 's' }} added
            </span>
          </li>
          <li class="summary-row" *ngIf="connectedCrmProvider">
            <span class="summary-check">✓</span>
            <span class="summary-text">CRM connected ({{ connectedCrmLabel }})</span>
          </li>
        </ul>

        <p class="summary-hint" *ngIf="hasProcessingFiles">
          Processing is still running in the background. You can continue and we will update status in the dashboard.
        </p>

        <p class="summary-hint" *ngIf="!connectedCrmProvider">
          CRM not connected yet. You can connect it later in Settings.
        </p>

        <button class="btn-primary complete-cta" (click)="goToDashboard()" [class.complete-cta--exiting]="isExitingDashboard" [disabled]="isExitingDashboard">
          <span *ngIf="!isExitingDashboard" class="complete-cta__content">
            <span class="complete-cta__label">Head to dashboard</span>
            <fa-icon [icon]="faArrowRight" class="complete-cta__icon"></fa-icon>
          </span>
          <span *ngIf="isExitingDashboard" class="loading-dots complete-cta__loading">
            <span></span><span></span><span></span>
          </span>
        </button>
      </div>
    </div>

  </div>
</div>
